import React, { useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Box, TableBody, TableRow, TableCell, Icons } from '@curves/ui-base';
import { Table, ScrollerArrowButton } from '@curves/ui-shared';
import {ListEmptyState} from '@clubInventory/components/';

/**
 * InventoryProductList - 庫存商品清單組件（配置化共用組件）
 *
 * @param {Array} products - 商品列表
 * @param {Array} headerRow - 表格欄位配置（與 Table 組件的 headerRow 格式相同）
 * @param {string} emptyMessage - 空狀態提示訊息
 * @param {React.ReactNode} emptyIcon - 空狀態圖示（可選）
 * @param {string|number} height - 容器固定高度（可選）
 * @param {string} className - 自定義樣式類名
 * @param {string} rowKey - 行的唯一鍵，預設為 'productID'
 * @param {Function} onQuantityChange - 數量變更回調（由 SkuAddToList.List 注入）
 * @param {Function} onOpenQuantityEdit - 點擊數量開啟編輯彈窗回調（由 SkuAddToList.List 注入）
 * @param {Function} onDelete - 刪除商品回調（由 SkuAddToList.List 注入）
 * @param {Function} getProductKey - 取得商品唯一鍵（由 SkuAddToList.List 注入）
 * @param {...any} otherProps - 其他 props 會傳遞給 Table.Body，進而傳遞給 formatCell 的第三個參數
 */
const InventoryProductList = ({
    products = [],
    headerRow = [],
    emptyMessage = "尚無資料",
    emptyIcon = <Icons.HandheldScanner style={{ fontSize: '210px' }} />,
    height,
    className = "",
    rowKey = "productID",
    onQuantityChange,
    onOpenQuantityEdit,
    onDelete,
    getProductKey,
    ...otherProps
}) => {
    const scrollerRef = useRef(null);

    // 外層容器樣式：統一管理固定高度
    const containerStyle = {
        ...(height && { height }),
    };

    const isEmpty = products.length === 0;

    return (
        <Box
            className={clsx("inventory-product-list-container scroll-arrow-container", className, isEmpty && "inventory-product-list-empty")}
            style={containerStyle}
        >
            <Table
                tableContainerRef={scrollerRef}
                rowKey={rowKey}
            >
                {/* 無論有無資料都顯示 Header */}
                <Table.Header headerRow={headerRow} />

                {/* 根據資料狀態渲染不同的 Body */}
                {isEmpty ? (
                    // 無資料時：自定義 ListEmptyState，佔滿剩餘空間
                    <TableBody>
                        <TableRow>
                            <TableCell
                                colSpan={headerRow.length}
                                className="empty-state-cell"
                                style={{
                                    padding: 0,
                                    height: "100%",
                                    border: "none"
                                }}
                            >
                                <ListEmptyState
                                    message={emptyMessage}
                                    icon={emptyIcon}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                ) : (
                    // 有資料時：使用 Table.Body 渲染資料，將 onQuantityChange/onDelete/getProductKey 傳遞給 formatCell
                    <Table.Body
                        headerRow={headerRow}
                        rowsData={products}
                        onQuantityChange={onQuantityChange}
                        onOpenQuantityEdit={onOpenQuantityEdit}
                        onDelete={onDelete}
                        getProductKey={getProductKey}
                        {...otherProps}
                    />
                )}
            </Table>

            {/* 標配滾動箭頭（僅在有資料時顯示） */}
            {!isEmpty && <ScrollerArrowButton parentRef={scrollerRef} />}
        </Box>
    );
};

InventoryProductList.propTypes = {
    products: PropTypes.array, // 由 SkuAddToList.List 注入，預設為 []
    headerRow: PropTypes.arrayOf(
        PropTypes.shape({
            cellKey: PropTypes.string.isRequired,
            headerLabel: PropTypes.string.isRequired,
            align: PropTypes.oneOf(["left", "center", "right"]),
            formatCell: PropTypes.func,
            width: PropTypes.number,
            minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
    ).isRequired,
    emptyMessage: PropTypes.string,
    emptyIcon: PropTypes.node,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    onQuantityChange: PropTypes.func,
    onOpenQuantityEdit: PropTypes.func,
    onDelete: PropTypes.func,
    getProductKey: PropTypes.func,
};

export default InventoryProductList;
